import React, { useState, useCallback } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Alert,
  IconButton,
  Tooltip,
  Popover,
  Collapse,
  Button,
} from "@mui/material";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExpandLess from "@mui/icons-material/ExpandLess";
import { useChatbotMutations } from "../../hooks/useChatbot";
import { ChatbotFlowEditor } from "./index";
import ChatbotTreeView from "./ChatbotTreeView";
import ChatbotNodePanel from "./ChatbotNodePanel";
import ChatbotSimulator from "./ChatbotSimulator";
import { useTranslation } from "react-i18next";

/**
 * Área do editor de fluxo (canvas/árvore + simulador), sem cabeçalho de rota.
 */
export default function ChatbotFlowWorkspace({ userId, error, setError }) {
  const { t } = useTranslation("settings");
  const { createSlot, updateSlot, deleteSlot } = useChatbotMutations(userId);
  const [viewMode, setViewMode] = useState("canvas");
  const [selectedSlotTree, setSelectedSlotTree] = useState(null);
  const [isTreePanelOpen, setIsTreePanelOpen] = useState(false);
  const [legendAnchor, setLegendAnchor] = useState(null);
  const [showHelp, setShowHelp] = useState(false);

  const handleEmptyInit = useCallback(() => {
    if (!userId) return;
    setError(null);
    createSlot.mutate(
      { user_id: userId, parent_id: 0, identificador: "Início", criar_excecao: true },
      {
        onSuccess: () => setError(null),
        onError: (err) => setError(err?.message || t("chatbotConfigPage.errorInit")),
      }
    );
  }, [userId, createSlot, setError, t]);

  const handleTreeSaveSlot = (payload) => {
    if (!payload?.id) return;
    updateSlot.mutate(payload, {
      onSuccess: () => {
        setError(null);
        setIsTreePanelOpen(false);
      },
      onError: (err) => setError(err?.message || t("chatbotConfigPage.errorSaveSlot")),
    });
  };

  const handleTreeDeleteSlot = (id) => {
    if (!id) return;
    deleteSlot.mutate(id, {
      onSuccess: () => {
        setError(null);
        setIsTreePanelOpen(false);
        setSelectedSlotTree(null);
      },
      onError: (err) => setError(err?.message || t("chatbotConfigPage.errorDeleteSlot")),
    });
  };

  const handleTreeAddChild = (parentSlot) => {
    if (!parentSlot || !userId) return;
    createSlot.mutate(
      {
        user_id: userId,
        parent_id: parentSlot.id,
        identificador: "Novo passo",
        criar_excecao: true,
        global: 0,
        ignorarglobal: 0,
      },
      {
        onSuccess: (created) => {
          setError(null);
          if (created?.id != null) {
            setSelectedSlotTree(created);
            setIsTreePanelOpen(true);
          }
        },
        onError: (err) => setError(err?.message || t("chatbotConfigPage.errorCreateSubstep")),
      }
    );
  };

  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        borderRadius: 1,
        p: 2,
        boxShadow: (th) => th.shadows[1],
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1.5,
          mb: 1,
          minHeight: 40,
        }}
      >
        <Tooltip title={t("chatbotConfigPage.legendTooltip")}>
          <IconButton
            size="small"
            onClick={(e) => setLegendAnchor(e.currentTarget)}
            aria-label={t("chatbotConfigPage.legendTitle")}
          >
            <InfoOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
        <Popover
          open={!!legendAnchor}
          anchorEl={legendAnchor}
          onClose={() => setLegendAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        >
          <Box sx={{ p: 2, maxWidth: 420 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              {t("chatbotConfigPage.legendTitle")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              {t("chatbotConfigPage.legendBody")}
            </Typography>
          </Box>
        </Popover>
        <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
          <Tabs
            value={viewMode}
            onChange={(_, v) => setViewMode(v)}
            textColor="primary"
            indicatorColor="primary"
            sx={{
              minHeight: 32,
              "& .MuiTab-root": { minHeight: 32, textTransform: "none", fontSize: 13, px: 1.5 },
            }}
          >
            <Tab label={t("chatbotConfigPage.tabCanvas")} value="canvas" />
            <Tab label={t("chatbotConfigPage.tabTree")} value="tree" />
          </Tabs>
          <Button
            size="small"
            endIcon={showHelp ? <ExpandLess /> : <ExpandMore />}
            onClick={() => setShowHelp((v) => !v)}
            sx={{ textTransform: "none" }}
          >
            {t("chatbotConfigPage.learnMore")}
          </Button>
        </Box>
      </Box>

      <Collapse in={showHelp}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.5 }}>
          {t("chatbotConfigPage.helpCanvas")}
        </Typography>
      </Collapse>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        {viewMode === "canvas" ? (
          <ChatbotFlowEditor userId={userId} onEmptyInit={handleEmptyInit} />
        ) : (
          <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
            <Box sx={{ flex: 1.2, minWidth: 260, pr: 1.5 }}>
              <ChatbotTreeView
                userId={userId}
                selectedSlotId={selectedSlotTree?.id ?? null}
                onSelectSlot={(slot) => {
                  setSelectedSlotTree(slot);
                  setIsTreePanelOpen(true);
                }}
                onAddChild={handleTreeAddChild}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 260 }}>
              <ChatbotNodePanel
                slot={isTreePanelOpen ? selectedSlotTree : null}
                onClose={() => setIsTreePanelOpen(false)}
                onSave={handleTreeSaveSlot}
                onDelete={handleTreeDeleteSlot}
                onAddSubpasso={
                  selectedSlotTree && selectedSlotTree.identificador !== "Exceção"
                    ? () => handleTreeAddChild(selectedSlotTree)
                    : undefined
                }
                isSaving={updateSlot.isLoading}
                isDeleting={deleteSlot.isLoading}
                isAddingSubpasso={createSlot.isLoading}
                saveError={null}
                canDelete={
                  !!selectedSlotTree &&
                  selectedSlotTree.identificador !== "Exceção" &&
                  selectedSlotTree.identificador !== "Início"
                }
              />
            </Box>
          </Box>
        )}
      </Box>
      <ChatbotSimulator userId={userId} />
    </Box>
  );
}
